// the photo rail overflows on every viewport narrower than the 1920 figma frame.
// it opens on the middle of the row and every stop centres a photo, at every
// width (Rustam, Oct 1: the desktop row used to park photos flush left). on
// desktop the two arrows page it a photo at a time and a mouse can drag it,
// since a desktop pointer has no gesture for an overflow-x box; a drag or a
// flick settles on the nearest photo once it lets go.
//
// the row also loops: it is cloned into whole repeating cycles either side of
// the real one, so scrolling in either direction never runs out of photos. once
// the rail leaves the live cycle it is jumped back by exactly one cycle width,
// which nobody can see because the photos at that offset are the same ones.
(() => {
  const TRACK = '.real-life-benchmark__track';
  const DRAGGING = 'real-life-benchmark__track--dragging';
  const DESKTOP = '(min-width: 750px)';
  // past this the pointer is scrolling the rail, not clicking a photo
  const DRAG_THRESHOLD = 4;
  // a flick keeps gliding after release instead of stopping dead under the cursor
  const GLIDE_FRICTION = 0.94;
  const GLIDE_MIN_SPEED = 0.02;
  // how long the rail has to stop moving before the loop jump is safe to make
  const IDLE_MS = 120;
  // how far a hard flick can still carry the rail after the finger leaves it.
  // the clone buffer holds this much run-off past the live cycle so momentum is
  // never cut short by the jump
  const FLING_REACH = 3000;
  const states = new WeakMap();

  const isDesktop = () => window.matchMedia(DESKTOP).matches;

  const rail = (track) => track.closest('.real-life-benchmark__rail');

  const padLeftOf = (track) => parseFloat(getComputedStyle(track).paddingLeft) || 0;

  // scroll offsets that centre each photo, which is where mandatory snapping
  // lands anyway
  const stops = (track) =>
    [...track.children].map((slide) => slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2);

  // a mouse drag or its glide runs with snapping off, so when it ends the rail is
  // carried to the photo nearest the middle instead of stopping between two
  const settle = (track) => {
    const current = track.scrollLeft;
    const nearest = stops(track).reduce(
      (best, stop) => (Math.abs(stop - current) < Math.abs(best - current) ? stop : best),
      current
    );
    if (Math.abs(nearest - current) > 1) track.scrollTo({ left: nearest, behavior: 'smooth' });
  };

  const cloneSlide = (slide) => {
    const clone = slide.cloneNode(true);
    clone.dataset.rlbClone = 'true';
    // the copies are the same photos read out again, and nothing in them is
    // focusable, so the screen reader only needs the real row
    clone.setAttribute('aria-hidden', 'true');
    // the entrance belongs to the real row: a clone carrying pre-reveal would
    // sit invisible where no observer is watching it, and a copied editor id
    // would give the theme editor two homes for one block
    clone.removeAttribute('data-reveal');
    clone.removeAttribute('style');
    clone.removeAttribute('data-shopify-editor-block');
    clone.classList.remove('pre-reveal');
    clone.classList.add('in-view');
    return clone;
  };

  // the cycle is the real row plus the gap that follows it, so laying cycles end
  // to end reproduces the same rhythm the row already has
  const measure = (track, state) => {
    const { originals } = state;
    state.unitWidth = 0;
    if (originals.length < 2) return;

    const first = originals[0];
    const last = originals[originals.length - 1];
    const gap = parseFloat(getComputedStyle(track).columnGap) || 0;
    const unit = last.offsetLeft + last.offsetWidth - first.offsetLeft + gap;
    if (unit <= 0) return;

    state.unitWidth = unit;
    state.bandStart = first.offsetLeft - padLeftOf(track);
    // the slides carry fixed sizes, so the scroll width only moves when the row
    // is rebuilt or the viewport changes, both of which land back here
    state.maxScroll = track.scrollWidth - track.clientWidth;
  };

  // enough cycles either side to cover the viewport plus a whole fling, so the
  // rail almost always comes to rest before the jump is needed
  const cyclesFor = (track, unit) => Math.ceil((track.clientWidth + FLING_REACH) / unit) + 1;

  // returns where the rail was sitting inside its cycle, as a fraction, so a
  // rebuild can put the reader back on the same photo
  const buildLoop = (track, state) => {
    const previous = state.unitWidth ? (track.scrollLeft - state.bandStart) / state.unitWidth : null;

    track.querySelectorAll('[data-rlb-clone]').forEach((clone) => clone.remove());
    state.originals = [...track.children];
    state.cycles = 0;
    measure(track, state);
    if (!state.unitWidth) return previous;

    const cycles = cyclesFor(track, state.unitWidth);
    const before = document.createDocumentFragment();
    const after = document.createDocumentFragment();
    for (let cycle = 0; cycle < cycles; cycle += 1) {
      state.originals.forEach((slide) => {
        before.appendChild(cloneSlide(slide));
        after.appendChild(cloneSlide(slide));
      });
    }
    track.insertBefore(before, state.originals[0]);
    track.appendChild(after);

    state.cycles = cycles;
    measure(track, state);
    return previous;
  };

  const ensureLoop = (track, state) => {
    const short = state.unitWidth && state.cycles < cyclesFor(track, state.unitWidth);
    // an unmeasurable first pass (stylesheet still settling) is worth retrying,
    // and a viewport that grew past the buffer needs more cycles behind it
    const rebuild = !state.built || short || (!state.unitWidth && state.originals.length > 1);

    if (!rebuild) {
      measure(track, state);
      return null;
    }

    state.built = true;
    return buildLoop(track, state);
  };

  // pulls the rail back into the live cycle by whole cycles, so the photos under
  // the viewport are identical before and after. returns how far it moved
  const wrapIntoBand = (track, state) => {
    if (!state.unitWidth) return 0;

    const steps = Math.floor((track.scrollLeft - state.bandStart) / state.unitWidth);
    if (steps === 0) return 0;

    const delta = steps * state.unitWidth;
    track.scrollLeft -= delta;
    return delta;
  };

  // a cycle from either end of the cloned row: waiting for the rail to settle
  // past this point risks a scroll fast enough to reach the real end between
  // two scroll events, which is where the row would stop dead
  const nearEdge = (track, state) =>
    track.scrollLeft < state.unitWidth || track.scrollLeft > state.maxScroll - state.unitWidth;

  const scrollToStop = (track, direction) => {
    const state = states.get(track);
    // paging from inside the live cycle keeps the smooth scroll clear of the
    // ends, so the loop never has to jump while the browser is animating
    if (state) wrapIntoBand(track, state);

    const max = track.scrollWidth - track.clientWidth;
    const current = track.scrollLeft;
    const candidates = stops(track)
      .map((stop) => Math.max(0, Math.min(max, stop)))
      // a stop the rail is already parked at must not count as the next one
      .filter((stop) => (direction > 0 ? stop > current + 1 : stop < current - 1));

    if (!candidates.length) return;
    track.scrollTo({ left: direction > 0 ? Math.min(...candidates) : Math.max(...candidates), behavior: 'smooth' });
  };

  const syncArrows = (track) => {
    const container = rail(track);
    if (!container) return;

    // a looped row always overflows and has no first or last photo to park
    // against, so it needs no measuring on every scroll event
    const looping = Boolean(states.get(track)?.unitWidth);
    const max = looping ? Infinity : track.scrollWidth - track.clientWidth;
    const prev = container.querySelector('[data-rlb-prev]');
    const next = container.querySelector('[data-rlb-next]');

    [prev, next].forEach((button) => button && (button.hidden = max <= 1));
    // 1px of slack: fractional layout widths leave the end a hair short of max
    if (prev) prev.disabled = !looping && track.scrollLeft <= 1;
    if (next) next.disabled = !looping && track.scrollLeft >= max - 1;
  };

  const centre = (track) => {
    const state = states.get(track);
    if (!state) return;

    const restore = ensureLoop(track, state);
    const originals = state.originals;
    const touched = track.dataset.rlbTouched === 'true';

    if (touched && restore !== null && state.unitWidth) {
      // a rebuilt row keeps whoever was reading it on the photo they were on
      track.scrollLeft = state.bandStart + restore * state.unitWidth;
      wrapIntoBand(track, state);
    } else if (!touched && track.scrollWidth > track.clientWidth) {
      // the row opens on the upper of the two middle photos, centred
      const slide = originals[Math.floor(originals.length / 2)];
      if (slide) track.scrollLeft = slide.offsetLeft - (track.clientWidth - slide.offsetWidth) / 2;
    }

    syncArrows(track);
  };

  const centreAll = () => document.querySelectorAll(TRACK).forEach(centre);

  const initTrack = (track, state) => {
    let startX = 0;
    let moved = false;
    let velocity = 0;
    let lastX = 0;
    let lastTime = 0;
    let glideFrame = 0;

    // snapping stays off (the dragging class) until the hand and its glide are
    // done, or mandatory snapping would catch every frame the script sets
    const release = () => {
      track.classList.remove(DRAGGING);
      settle(track);
    };

    const stopGlide = () => {
      if (!glideFrame) return;
      cancelAnimationFrame(glideFrame);
      glideFrame = 0;
      track.classList.remove(DRAGGING);
    };

    const glide = () => {
      velocity *= GLIDE_FRICTION;
      const before = track.scrollLeft;
      track.scrollLeft = before + velocity * 16;
      // a rail that stopped moving under its own momentum has nothing left to
      // spend; on a looped row only friction ever gets it there
      if (Math.abs(velocity) < GLIDE_MIN_SPEED || track.scrollLeft === before) {
        glideFrame = 0;
        wrapIntoBand(track, state);
        return release();
      }
      glideFrame = requestAnimationFrame(glide);
    };

    const onPointerMove = (event) => {
      if (!state.dragging) return;

      const delta = event.clientX - startX;
      if (!moved && Math.abs(delta) > DRAG_THRESHOLD) {
        moved = true;
        track.classList.add(DRAGGING);
        track.dataset.rlbTouched = 'true';
      }
      if (!moved) return;

      track.scrollLeft = state.startScroll - delta;

      const now = performance.now();
      const elapsed = now - lastTime;
      // the rail moves against the pointer, so a leftward drag is a positive speed
      if (elapsed > 0) velocity = (lastX - event.clientX) / elapsed;
      lastX = event.clientX;
      lastTime = now;
    };

    const endDrag = () => {
      if (!state.dragging) return;
      state.dragging = false;
      // the jump the drag was holding off can be made now that the rail is the
      // browser's again, and it is invisible whether or not a glide follows
      wrapIntoBand(track, state);
      if (!moved) return track.classList.remove(DRAGGING);

      // a drag that ends over a photo would otherwise fire a click on it
      track.addEventListener('click', (event) => event.stopPropagation(), { capture: true, once: true });
      if (Math.abs(velocity) > GLIDE_MIN_SPEED) glideFrame = requestAnimationFrame(glide);
      else release();
    };

    const onScroll = () => {
      syncArrows(track);
      if (!state.unitWidth) return;

      // the jump lands on identical photos but still cancels native momentum,
      // so it waits for the rail to settle unless an end is close enough to hit
      if (nearEdge(track, state)) {
        const delta = wrapIntoBand(track, state);
        // the drag reads its position off the scroll it started from, so that
        // anchor has to travel with the jump or the photos snap back
        if (delta && state.dragging) state.startScroll -= delta;
        return;
      }

      clearTimeout(state.idle);
      state.idle = setTimeout(() => {
        if (state.dragging) return;
        wrapIntoBand(track, state);
      }, IDLE_MS);
    };

    track.addEventListener('pointerdown', (event) => {
      stopGlide();
      // touch and pen already scroll natively, and hijacking them breaks momentum
      if (event.pointerType !== 'mouse' || event.button !== 0 || !isDesktop()) return;

      state.dragging = true;
      moved = false;
      velocity = 0;
      startX = event.clientX;
      lastX = event.clientX;
      lastTime = performance.now();
      state.startScroll = track.scrollLeft;
    });

    track.addEventListener('pointermove', onPointerMove);
    track.addEventListener('pointerup', endDrag);
    track.addEventListener('pointercancel', endDrag);
    // releasing outside the rail still ends the drag
    window.addEventListener('pointerup', endDrag);
    track.addEventListener('dragstart', (event) => event.preventDefault());
    track.addEventListener('wheel', stopGlide, { passive: true });
    track.addEventListener('scroll', onScroll, { passive: true });
  };

  const init = () => {
    document.querySelectorAll(TRACK).forEach((track) => {
      if (!states.has(track)) {
        const state = {
          originals: [...track.children],
          unitWidth: 0,
          bandStart: 0,
          maxScroll: 0,
          cycles: 0,
          built: false,
          dragging: false,
          startScroll: 0,
          idle: 0,
        };
        states.set(track, state);
        initTrack(track, state);
      }
      centre(track);
    });
  };

  init();
  // mandatory snapping on mobile re-runs its own initial pass after first layout
  // and can pull the row back to photo one, so claim the position again
  requestAnimationFrame(centreAll);
  // and once more after load, since scroll restoration lands after that and the
  // images that were still decoding are what settle the final scroll width
  window.addEventListener('load', centreAll);

  document.addEventListener('click', (event) => {
    const button = event.target.closest('[data-rlb-prev], [data-rlb-next]');
    if (!button) return;

    const track = rail(button)?.querySelector(TRACK);
    if (!track) return;

    track.dataset.rlbTouched = 'true';
    scrollToStop(track, button.hasAttribute('data-rlb-next') ? 1 : -1);
  });

  // once the visitor moves the rail themselves, their position wins
  ['pointerdown', 'touchstart', 'wheel', 'keydown'].forEach((type) => {
    document.addEventListener(
      type,
      (event) => {
        const track = event.target.closest?.(TRACK);
        if (track) track.dataset.rlbTouched = 'true';
      },
      { passive: true, capture: true }
    );
  });

  // devtools device-mode toggles and orientation changes cross into a different
  // rail width after load, so re-measure on real width changes only. ios collapsing
  // its url bar fires resize with the same width, and that must not yank the rail
  // back while someone is reading it
  let lastWidth = window.innerWidth;
  window.addEventListener('resize', () => {
    if (window.innerWidth === lastWidth) return;
    lastWidth = window.innerWidth;
    centreAll();
  });

  document.addEventListener('shopify:section:load', init);
})();
